import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { cn, formatPrice } from '@/lib/utils'
import {
  apartmentAnalyticsProps,
  trackEvent
} from '@services/livability/analytics'
import {
  chatSearchStream,
  deleteChatSession
} from '@services/livability/search'
import { useListingModeStore } from '@services/livability/store'
import type {
  ChatSearchApartment,
  ChatSearchResponse
} from '@services/livability/types'
import {
  Bot,
  DoorOpen,
  Loader2,
  Map,
  MapPin,
  RotateCcw,
  Ruler,
  Send,
  Sparkles,
  User
} from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useLivabilityPaths } from '../utils/paths'

const CHAT_STORAGE_KEY = 'livability-chat-state'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  apartments?: ChatSearchApartment[]
  filters?: Record<string, any>
  hasMore?: boolean
  totalCount?: number
  lastQuery?: string
  timestamp: Date
}

interface StoredChatState {
  sessionId: string | null
  messages: Array<Omit<Message, 'timestamp'> & { timestamp: string }>
}

function loadStoredState(): StoredChatState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredChatState
  } catch {
    return null
  }
}

function saveStoredState(sessionId: string | null, messages: Message[]) {
  if (typeof window === 'undefined') return
  const payload: StoredChatState = {
    sessionId,
    messages: messages.map((m) => ({
      ...m,
      timestamp: m.timestamp.toISOString()
    }))
  }
  sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(payload))
}

function ApartmentResultCard({
  apartment
}: {
  apartment: ChatSearchApartment
}) {
  const paths = useLivabilityPaths()
  const { t } = useTranslation('livability')

  return (
    <Link
      to={paths.detail(apartment.id)}
      onClick={() =>
        trackEvent(
          'apartment_clicked',
          apartmentAnalyticsProps(
            {
              id: apartment.id,
              price_month: apartment.price_month,
              room_count: apartment.room_count,
              area_m2: apartment.area_m2,
              location: apartment.location
            },
            'chat'
          )
        )
      }
    >
      <Card className="group h-full cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(4,33,37,0.1)]">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="line-clamp-2 font-display text-base font-bold leading-snug tracking-tight">
              {apartment.title}
            </CardTitle>
            {apartment.index_score != null && (
              <span className="inline-flex shrink-0 items-center rounded-full bg-info-soft px-2.5 py-0.5 text-xs font-semibold text-info">
                {t('card.index', { score: apartment.index_score })}
              </span>
            )}
          </div>
          <CardDescription className="flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{apartment.location}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-xl font-bold text-primary">
              {formatPrice(apartment.price_month)}
            </span>
            {(apartment.listing_type ?? 'rent') === 'rent' &&
              apartment.price_month > 0 && (
                <span className="text-sm text-muted-foreground">
                  {t('card.perMonth')}
                </span>
              )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {apartment.area_m2 > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-ink/[0.04] px-2.5 py-0.5 text-xs font-medium text-ink/70">
                <Ruler className="h-3 w-3" />
                {apartment.area_m2} m²
              </span>
            )}
            {apartment.room_count > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-ink/[0.04] px-2.5 py-0.5 text-xs font-medium text-ink/70">
                <DoorOpen className="h-3 w-3" />
                {t('card.rooms', { count: apartment.room_count })}
              </span>
            )}
            {apartment.furnishing && apartment.furnishing !== 'None' && (
              <span className="inline-flex items-center rounded-full bg-ink/[0.04] px-2.5 py-0.5 text-xs font-medium text-ink/70">
                {apartment.furnishing}
              </span>
            )}
            {apartment.parking_type && apartment.parking_type !== 'None' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-ink/[0.04] px-2.5 py-0.5 text-xs font-medium text-ink/70">
                🅿️ {apartment.parking_type}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

function ActiveFiltersDisplay({ filters }: { filters: Record<string, any> }) {
  const { t } = useTranslation('livability')
  const filterEntries = Object.entries(filters).filter(
    ([key, value]) =>
      value !== null &&
      value !== undefined &&
      !['offset', 'limit', 'sort_by'].includes(key)
  )

  if (filterEntries.length === 0) return null

  const formatFilterValue = (key: string, value: any): string => {
    if (typeof value === 'boolean')
      return value ? t('filters.yes') : t('filters.no')
    if (key === 'listing_type') {
      return value === 'sale'
        ? t('filters.listingTypeSale')
        : t('filters.listingTypeRent')
    }
    if (key.includes('price')) return `${value} €`
    if (key.includes('area')) return `${value} m²`
    if (key === 'rank_by') {
      return value === 'value'
        ? t('filters.rankByValue')
        : t('filters.rankByQuality')
    }
    return String(value)
  }

  const formatFilterKey = (key: string): string =>
    t(`filters.keys.${key}`, { defaultValue: key })

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {filterEntries.map(([key, value]) => (
        <Badge key={key} variant="secondary" className="text-xs">
          {formatFilterKey(key)}: {formatFilterValue(key, value)}
        </Badge>
      ))}
    </div>
  )
}

function TypingIndicator() {
  const { t } = useTranslation('livability')
  return (
    <span
      className="flex items-center gap-1 py-1"
      role="status"
      aria-label={t('aiSearch.assistantTyping')}
    >
      <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.3s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.15s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60" />
    </span>
  )
}

function ChatMessage({
  message,
  onShowOnMap,
  onLoadMore,
  isLoadingMore
}: {
  message: Message
  onShowOnMap?: (apartments: ChatSearchApartment[]) => void
  onLoadMore?: (messageId: string) => void
  isLoadingMore?: boolean
}) {
  const { t } = useTranslation('livability')
  const isUser = message.role === 'user'

  return (
    <div
      className={cn(
        'flex gap-3 rounded-2xl p-4',
        isUser
          ? 'bg-primary/[0.05]'
          : 'border border-ink/[0.06] bg-white shadow-[0_6px_20px_rgba(4,33,37,0.04)]'
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-primary/10 text-primary'
        )}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {message.content ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
            {message.content}
          </p>
        ) : (
          !isUser && <TypingIndicator />
        )}

        {!isUser && message.filters && (
          <ActiveFiltersDisplay filters={message.filters} />
        )}

        {!isUser && message.apartments && message.apartments.length > 0 && (
          <>
            {message.totalCount != null && (
              <p className="text-xs text-muted-foreground">
                {t('aiSearch.showing', {
                  shown: message.apartments.length,
                  total: message.totalCount
                })}
              </p>
            )}
            <div className="grid gap-3 mt-4 sm:grid-cols-2">
              {message.apartments.map((apt) => (
                <ApartmentResultCard key={apt.id} apartment={apt} />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => onShowOnMap?.(message.apartments!)}
              >
                <Map className="h-4 w-4" />
                {t('aiSearch.showOnMap')}
              </Button>
              {message.hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isLoadingMore}
                  onClick={() => onLoadMore?.(message.id)}
                >
                  {isLoadingMore ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('aiSearch.loadMore')
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function ChatSearch() {
  const { t } = useTranslation('livability')
  const navigate = useNavigate()
  const paths = useLivabilityPaths()
  const listingMode = useListingModeStore((s) => s.listingMode)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMoreFor, setLoadingMoreFor] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const stored = loadStoredState()
    if (stored) {
      setSessionId(stored.sessionId)
      setMessages(
        stored.messages.map((m) => ({
          ...m,
          timestamp: new Date(m.timestamp)
        }))
      )
    }
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return
    saveStoredState(sessionId, messages)
  }, [hydrated, sessionId, messages])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const applyResponse = useCallback(
    (
      response: ChatSearchResponse,
      userQuery: string,
      appendToMessageId?: string
    ) => {
      if (response.session_id) {
        setSessionId(response.session_id)
      }

      if (appendToMessageId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === appendToMessageId
              ? {
                  ...msg,
                  apartments: [...(msg.apartments || []), ...response.data],
                  hasMore: response.has_more,
                  totalCount: response.total_count
                }
              : msg
          )
        )
        return
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.text,
        apartments: response.data,
        filters: response.filters,
        hasMore: response.has_more,
        totalCount: response.total_count,
        lastQuery: userQuery,
        timestamp: new Date()
      }
      setMessages((prev) => [...prev, assistantMessage])

      if (response.error) {
        setError(response.error)
      }
    },
    []
  )

  const runSearch = useCallback(
    async (
      trimmedInput: string,
      options?: { offset?: number; appendToMessageId?: string }
    ) => {
      let streamingMessageId: string | null = null

      if (!options?.appendToMessageId) {
        streamingMessageId = crypto.randomUUID()
        setMessages((prev) => [
          ...prev,
          {
            id: streamingMessageId!,
            role: 'assistant',
            content: '',
            timestamp: new Date()
          }
        ])
      }

      await chatSearchStream(
        trimmedInput,
        sessionId,
        {
          onToken: (token) => {
            if (!streamingMessageId) return
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingMessageId
                  ? {
                      ...msg,
                      content: msg.content + token
                    }
                  : msg
              )
            )
          },
          onDone: (response) => {
            if (response.session_id) {
              setSessionId(response.session_id)
            }

            if (options?.appendToMessageId) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === options.appendToMessageId
                    ? {
                        ...msg,
                        apartments: [
                          ...(msg.apartments || []),
                          ...response.data
                        ],
                        hasMore: response.has_more,
                        totalCount: response.total_count
                      }
                    : msg
                )
              )
              return
            }

            if (streamingMessageId) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMessageId
                    ? {
                        ...msg,
                        content: response.text,
                        apartments: response.data,
                        filters: response.filters,
                        hasMore: response.has_more,
                        totalCount: response.total_count,
                        lastQuery: trimmedInput
                      }
                    : msg
                )
              )
            } else {
              applyResponse(response, trimmedInput)
            }

            if (response.error) {
              setError(response.error)
            }
          },
          onError: (err) => {
            if (streamingMessageId) {
              setMessages((prev) =>
                prev.filter((m) => m.id !== streamingMessageId)
              )
            }
            throw err
          }
        },
        options?.offset ?? 0,
        listingMode
      )
    },
    [sessionId, applyResponse, listingMode]
  )

  const sendMessage = async (raw: string) => {
    const trimmedInput = raw.trim()
    if (!trimmedInput || isLoading) return

    setError(null)

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmedInput,
      timestamp: new Date()
    }
    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
      await runSearch(trimmedInput)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('aiSearch.unexpectedError')
      setError(errorMessage)

      const errorAssistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: t('aiSearch.errorPrefix', { message: errorMessage }),
        timestamp: new Date()
      }
      setMessages((prev) => [...prev, errorAssistantMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  const handleLoadMore = async (messageId: string) => {
    const target = messages.find((m) => m.id === messageId)
    if (!target?.lastQuery || loadingMoreFor) return

    setLoadingMoreFor(messageId)
    try {
      const offset = target.apartments?.length ?? 0
      await runSearch(target.lastQuery, {
        offset,
        appendToMessageId: messageId
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('aiSearch.unexpectedError')
      setError(errorMessage)
    } finally {
      setLoadingMoreFor(null)
    }
  }

  const handleNewConversation = async () => {
    if (sessionId) {
      try {
        await deleteChatSession(sessionId)
      } catch {
        // Best-effort server cleanup
      }
    }
    setMessages([])
    setSessionId(null)
    setError(null)
    setInput('')
    sessionStorage.removeItem(CHAT_STORAGE_KEY)
    inputRef.current?.focus()
  }

  const handleShowOnMap = (apartments: ChatSearchApartment[]) => {
    navigate(paths.mapWithIds(apartments.map((a) => a.id)))
  }

  const exampleQueries = t('aiSearch.examples', {
    returnObjects: true
  }) as string[]

  if (!hydrated) {
    return (
      <div className="app-chrome flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="app-chrome flex min-h-0 w-full flex-1 flex-col">
      {messages.length > 0 && (
        <div className="flex justify-end border-b border-ink/[0.06] px-4 py-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewConversation}
            className="gap-1"
          >
            <RotateCcw className="h-4 w-4" />
            {t('aiSearch.newConversation')}
          </Button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center">
            <div className="flex w-full max-w-lg flex-col items-center gap-6 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles className="h-7 w-7" />
              </span>
              <h3 className="m-0 font-display text-lg font-bold text-ink">
                {t('aiSearch.emptyTitle')}
              </h3>
              <div className="w-full space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground pb-4">
                  {t('aiSearch.tryExamples')}
                </p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {exampleQueries.map((query, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => sendMessage(query)}
                      disabled={isLoading}
                      className="group flex items-center gap-2 rounded-2xl border border-ink/[0.08] bg-white px-4 py-3 text-left text-sm text-ink shadow-[0_4px_16px_rgba(4,33,37,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_10px_28px_rgba(17,173,187,0.14)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Sparkles className="h-4 w-4 shrink-0 text-primary/70 transition-colors group-hover:text-primary" />
                      <span>{query}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                onShowOnMap={handleShowOnMap}
                onLoadMore={handleLoadMore}
                isLoadingMore={loadingMoreFor === message.id}
              />
            ))}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {error && (
        <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="shrink-0 border-t border-ink/[0.06] bg-background/80 p-4 backdrop-blur-sm">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('aiSearch.inputPlaceholder')}
            disabled={isLoading}
            className="flex-1"
          />
          <Button
            type="submit"
            size="icon"
            aria-label={t('aiSearch.send')}
            disabled={isLoading || !input.trim()}
            className="h-11 w-11 shrink-0 rounded-full"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>

        {sessionId && messages.length > 0 && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {t('aiSearch.conversationActive')}
          </p>
        )}
      </div>
    </div>
  )
}
